import {
  IconProps,
  NavTalkCalendarActive,
  NavHomeActive,
  Chart,
  StackPerspective,
  Chat,
  Phone,
  Crowd,
} from 'icons';
import React, { useEffect, useState, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import useIsManagerExperienceActive from 'Home/Managers/useIsManagerExperienceActive';
import { useUnmindTalk } from 'Talk/hooks';
import { useOrganisationEntitlements } from 'Shared/Entitlement';
import RoutePath from '../App/RoutePath';
import useFeatureFlag, { FEATURE_FLAGS } from '../flags/useFeatureFlag';
import HorizontalMenu from './HorizontalMenu/HorizontalMenu';
import VerticalMenu from './VerticalMenu/VerticalMenu';

export enum MainMenuFeatures {
  TODAY = 'today',
  MANAGERS = 'managers',
  EXPLORE = 'explore',
  TRACK = 'track',
  TALK = 'talk',
  CHATBOT = 'chatbot',
  HELP = 'help',
}

export interface MainMenuProps {
  isInFooter: boolean;
  logoComponent?: ReactNode;
  orientation: 'vertical' | 'horizontal';
}

interface LinkConfig {
  name: string;
  to: string;
  Icon: React.FC<IconProps>;
  shouldHide?: boolean;
  feature?: MainMenuFeatures;
  label?: string;
}

const useActiveFeature = () => {
  const location = useLocation();
  const featurePath = `/${location.pathname.split('/')[1]}`;
  const [featureHistory, setFeatureHistory] = useState<string[]>([]);
  const [activeFeature, setActiveFeature] = useState<
    MainMenuFeatures | undefined
  >(undefined);

  // eslint-disable-next-line complexity
  useEffect(() => {
    if (featurePath !== featureHistory[featureHistory.length - 1]) {
      setFeatureHistory([...featureHistory, featurePath]);
    }

    switch (featurePath) {
      case RoutePath.Home:
      case RoutePath.CheatSheet:
        setActiveFeature(MainMenuFeatures.TODAY);
        break;
      case RoutePath.Managers:
        setActiveFeature(MainMenuFeatures.MANAGERS);
        break;
      case RoutePath.Explore:
      case RoutePath.YourCourses:
        setActiveFeature(MainMenuFeatures.EXPLORE);
        break;
      case RoutePath.Track:
      case RoutePath.UnmindIndex:
      case RoutePath.CheckIn:
        setActiveFeature(MainMenuFeatures.TRACK);
        break;
      case RoutePath.Talk:
        setActiveFeature(MainMenuFeatures.TALK);
        break;
      case '/coach':
        setActiveFeature(MainMenuFeatures.CHATBOT);
        break;
      case RoutePath.Help:
        setActiveFeature(MainMenuFeatures.HELP);
        break;
      case RoutePath.Courses:
      case RoutePath.Series:
        const trackFeatureIndex = featureHistory.lastIndexOf(RoutePath.Track);
        const exploreFeatureIndex = featureHistory.lastIndexOf(
          RoutePath.Explore,
        );
        const todayFeatureIndex = featureHistory.lastIndexOf(RoutePath.Home);
        const didRouteFromTrack =
          trackFeatureIndex > -1 &&
          trackFeatureIndex > exploreFeatureIndex &&
          trackFeatureIndex > todayFeatureIndex;
        const didRouteFromExplore =
          exploreFeatureIndex > -1 &&
          exploreFeatureIndex > trackFeatureIndex &&
          exploreFeatureIndex > todayFeatureIndex;
        const didRouteFromToday =
          todayFeatureIndex > -1 &&
          todayFeatureIndex > exploreFeatureIndex &&
          todayFeatureIndex > trackFeatureIndex;

        if (didRouteFromTrack) {
          setActiveFeature(MainMenuFeatures.TRACK);
        }

        if (didRouteFromExplore) {
          setActiveFeature(MainMenuFeatures.EXPLORE);
        }

        if (didRouteFromToday) {
          setActiveFeature(MainMenuFeatures.TODAY);
        }

        break;
      default:
        setActiveFeature(undefined);
    }
  }, [featurePath, featureHistory]);

  return { activeFeature, setActiveFeature };
};

const MainMenu = ({
  isInFooter,
  logoComponent,
  orientation,
}: MainMenuProps) => {
  const { unmindTalkIsAvailableForUser } = useUnmindTalk();
  const { t: translate } = useTranslation('navigation');
  const { activeFeature, setActiveFeature } = useActiveFeature();
  const { entitlements } = useOrganisationEntitlements([
    { entitlement: 'unmindHelp' },
  ]);

  const isChatBotEnabled = useFeatureFlag(FEATURE_FLAGS.ENABLE_CHATBOT);
  const isNovaPhase2Enabled = useFeatureFlag(FEATURE_FLAGS.NOVA_HOME_PHASE_2);

  const { isManagerExperienceActive } = useIsManagerExperienceActive();

  const showManagersTab = isNovaPhase2Enabled && isManagerExperienceActive;

  const homeLink = {
    name:
      isManagerExperienceActive && !isNovaPhase2Enabled
        ? translate('main_menu.tabs.for_managers')
        : translate('main_menu.tabs.home'),
    to: RoutePath.Home,
    feature: MainMenuFeatures.TODAY,
    Icon: NavHomeActive,
  };

  const exploreLink = {
    name: translate('main_menu.tabs.explore'),
    to: RoutePath.Explore,
    feature: MainMenuFeatures.EXPLORE,
    Icon: StackPerspective,
  };

  const trackLink = {
    name: translate('main_menu.tabs.track'),
    to: RoutePath.Track,
    feature: MainMenuFeatures.TRACK,
    Icon: Chart,
  };

  const talkLink = {
    name: translate('main_menu.tabs.talk'),
    to: RoutePath.Talk,
    feature: MainMenuFeatures.TALK,
    Icon: NavTalkCalendarActive,
    shouldHide: !unmindTalkIsAvailableForUser,
  };

  const chatbotLink = {
    name: translate('main_menu.tabs.chatbot'),
    to: RoutePath.Chatbot,
    feature: MainMenuFeatures.CHATBOT,
    Icon: Chat,
    shouldHide: !isChatBotEnabled,
  };

  const callLink = {
    name: translate('main_menu.tabs.help'),
    to: RoutePath.Help,
    feature: MainMenuFeatures.HELP,
    Icon: Phone,
    shouldHide: !entitlements.unmindHelp,
  };

  const managersLink = {
    name: translate('main_menu.tabs.managers'),
    to: RoutePath.Managers,
    feature: MainMenuFeatures.MANAGERS,
    Icon: Crowd,
    shouldHide: !showManagersTab,
  };

  const links: LinkConfig[] = [
    homeLink,
    managersLink,
    chatbotLink,
    talkLink,
    exploreLink,
    trackLink,
    callLink,
  ];

  if (orientation === 'vertical') {
    return (
      <VerticalMenu
        links={links}
        logoComponent={logoComponent}
        activeFeature={activeFeature}
        onFeatureClick={setActiveFeature}
      />
    );
  }

  return (
    <HorizontalMenu
      isInFooter={isInFooter}
      links={links}
      activeFeature={activeFeature}
      onFeatureClick={setActiveFeature}
    />
  );
};

export default MainMenu;
